import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";

const API_URL = "/api";

type Role = "megaadmin" | "super_admin" | "admin" | "receptionniste" | "protocole" | "organisateur";

interface User {
  id: string;
  name: string;
  phone: string;
  role: Role;
}

interface LoginResponse {
  success: boolean;
  token: string;
  user: User;
  message?: string;
}

interface AuthMeResponse {
  user: User;
}

interface RefreshTokenResponse {
  success: boolean;
  token: string;
  message?: string;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  setToken: (t: string | null) => void;
  logout: () => void;
  loading: boolean;
  login: (phone: string, password: string) => Promise<LoginResponse>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  token: null,
  setToken: () => {},
  logout: () => {},
  loading: true,
  login: async () => ({ success: false, token: "", user: { id: "", name: "", phone: "", role: "admin" } }),
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setTokenState] = useState<string | null>(localStorage.getItem("token"));
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setTokenState(null);
    setUser(null);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
  }, []);

  const refreshToken = useCallback(async (currentToken: string) => {
    try {
      const response = await axios.post<RefreshTokenResponse>(`${API_URL}/auth/refresh-token`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      
      if (response.data.success) {
        localStorage.setItem("token", response.data.token);
        setTokenState(response.data.token);
        console.log("Token rafraîchi avec succès");
      }
    } catch (error) {
      console.error("Erreur rafraîchissement token:", error);
      logout();
    }
  }, [logout]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    
    inactivityTimerRef.current = setTimeout(() => {
      console.log("Déconnexion pour inactivité (30 minutes)");
      logout();
      window.location.href = "/";
    }, 30 * 60 * 1000);
  }, [logout]);

  useEffect(() => {
    if (!token) return;
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });
    
    resetInactivityTimer();
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [token, resetInactivityTimer]);

  useEffect(() => {
    if (!token) return;
    
    refreshTimerRef.current = setInterval(() => {
      resetInactivityTimer();
      refreshToken(token);
    }, 50 * 60 * 1000);
    
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [token, refreshToken, resetInactivityTimer]);

  useEffect(() => {
    const fetchMe = async () => {
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get<AuthMeResponse>(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data.user);
      } catch (error: any) {
        if (error.response?.status === 401 || error.response?.status === 404) {
          localStorage.removeItem("token");
          setTokenState(null);
        }
        setUser(null);
      }
      setLoading(false);
    };
    fetchMe();
  }, [token]);

  const setToken = (t: string | null) => {
    if (t) {
      localStorage.setItem("token", t);
    } else {
      localStorage.removeItem("token");
    }
    setTokenState(t);
  };

  const login = async (phone: string, password: string) => {
    try {
      const response = await axios.post<LoginResponse>(`${API_URL}/auth/login`, {
        phone,
        password
      });

      if (response.data.success) {
        setToken(response.data.token);
        setUser(response.data.user);
      }

      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: "Erreur de connexion" };
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, setToken, logout, loading, login }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);